const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

/**
 * Relative time as an i18n descriptor: `{ key, count? }` where `key` is a child of
 * `members.relative.*` (the component translates it, so the util carries no copy).
 * Buckets: just now, minutes, hours, yesterday, days, weeks, months (30 d), years (365 d).
 *
 * @param {string|number|Date|null} date
 * @param {number} [now]
 * @returns {{ key: string, count?: number }|null}
 */
export const getRelativeTime = (date, now = Date.now()) => {
  if (!date) return null;

  const delta = Math.max(0, now - new Date(date).getTime());

  if (delta < MIN) return { key: 'justNow' };
  if (delta < HOUR) return { key: 'minutes', count: Math.floor(delta / MIN) };
  if (delta < DAY) return { key: 'hours', count: Math.floor(delta / HOUR) };
  if (delta < 2 * DAY) return { key: 'yesterday' };
  if (delta < 14 * DAY) return { key: 'days', count: Math.floor(delta / DAY) };
  if (delta < 30 * DAY) return { key: 'weeks', count: Math.floor(delta / (7 * DAY)) };
  if (delta < 365 * DAY) return { key: 'months', count: Math.floor(delta / (30 * DAY)) };

  return { key: 'years', count: Math.floor(delta / (365 * DAY)) };
};

const RELATIVE_UNITS = [
  ['month', 30 * DAY],
  ['week', 7 * DAY],
  ['day', DAY],
  ['hour', HOUR],
  ['minute', MIN],
];

/**
 * "16 hours ago", "yesterday", "3 weeks ago" in the given locale (via `Intl.RelativeTimeFormat`);
 * `null` when there is no date. The Groups member table uses it; the Members list uses
 * `getRelativeTime`, which hands the copy to the component.
 *
 * @param {string|null|undefined} iso
 * @param {string} [locale]
 * @param {number} [now]
 */
export const formatRelativeTime = (iso, locale = 'en', now = Date.now()) => {
  if (!iso) return null;
  const elapsed = now - new Date(iso).getTime();
  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const [unit, size] = RELATIVE_UNITS.find(([, ms]) => elapsed >= ms) ?? ['minute', MIN];

  return formatter.format(-Math.floor(elapsed / size), unit);
};
